/**
 * The staff console.
 *
 * Everything here is logged in the audit trail on the server, so the screen
 * does not try to be clever about permissions - it shows what this account
 * can reach and lets the server refuse the rest.
 */

import { useState } from 'react';
import { useRoute } from '@/lib/hash';
import { Page, PageTitle } from '@/components/page';
import { Icon } from '@/icons/Icon';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Party, PublicUser } from '@/lib/types';
import { cn } from '@/lib/cn';
import { go } from '@/lib/hash';
import { useSession } from '@/stores/session';
import { PageHead } from '@/components/PageHead';
import { Announcements, Reports } from './staff/Reports';
import { UserSheet } from './staff/UserSheet';
import { Community, type AdminOverview } from './staff/Community';
import { EmojiAdmin } from './staff/Emoji';
import { Permissions } from './staff/Permissions';
import { Avatar } from '@/components/Avatar';
import { RoleTag, TierTag, UserName } from '@/components/user';
import {
  Button, Card, CardHead, Empty, Input, Tag, useConfirm, useToast
} from '@/components/ui';

type Tab = 'overview' | 'users' | 'parties' | 'reports' | 'announce' | 'emoji' | 'perms' | 'audit' | 'server';

/**
 * The sections of the staff console.
 *
 * Exported because the header offers them on hover, so somebody can go
 * straight to Reports rather than landing on Overview and hunting. One list,
 * so the menu and the screen can never drift apart.
 */
export const STAFF_SECTIONS = [
  { id: 'overview', label: 'Overview', icon: 'chart' },
  { id: 'users', label: 'Users', icon: 'users' },
  { id: 'parties', label: 'Parties', icon: 'squads' },
  { id: 'reports', label: 'Reports', icon: 'flag' },
  { id: 'announce', label: 'News', icon: 'megaphone' },
  { id: 'emoji', label: 'Emoji', icon: 'heart' },
  { id: 'perms', label: 'Permissions', icon: 'key' },
  { id: 'audit', label: 'Audit', icon: 'clock' }
] as const;

export function StaffView() {
  const me = useSession((s) => s.me);
  const route = useRoute();
  const asked = route.parts[1];
  const tab: Tab = (asked && STAFF_SECTIONS.some((t) => t.id === asked) ? asked : 'overview') as Tab;
  const setTab = (next: Tab) => go('staff/' + next);

  // The badge is the point of a moderation queue - it has to be visible from
  // whichever tab you happen to be on.
  const overview = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: () => api.get<AdminOverview>('/api/admin/overview'),
    enabled: !!me && me.role !== 'user',
    refetchInterval: 30_000
  });
  const openReports = overview.data?.totals.openReports || undefined;

  if (!me || me.role === 'user') {
    return (
      <>
        <PageHead title="Restricted" crumb="Staff only" />
        <div className="p-[var(--pad)]">
          <Empty title="Not for you" note="This console is for moderators and admins." />
        </div>
      </>
    );
  }

  const TABS = STAFF_SECTIONS.map((t) =>
    t.id === 'reports' ? { ...t, badge: openReports } : t);

  const UNUSED_TABS = [
    { id: 'overview', label: 'Overview', icon: 'chart' },
    { id: 'server', label: 'Server', icon: 'bolt' },
    { id: 'users', label: 'Users', icon: 'users' },
    { id: 'parties', label: 'Parties', icon: 'squads' },
    { id: 'reports', label: 'Reports', icon: 'flag', badge: openReports },
    { id: 'announce', label: 'News', icon: 'megaphone' },
    { id: 'emoji', label: 'Emoji', icon: 'heart' },
    { id: 'perms', label: 'Permissions', icon: 'key' },
    { id: 'audit', label: 'Audit', icon: 'clock' }
  ] as const;
  void UNUSED_TABS;

  return (
    <Page wide>
      <PageTitle
        icon="shieldNav"
        title="Staff Console"
        note={
          openReports
            ? openReports + ' report' + (openReports === 1 ? '' : 's') + ' waiting on somebody'
            : 'All systems operational.'
        }
        actions={
          <span className="flex items-center gap-2 rounded-full border border-line-soft bg-surface-1 px-3.5 py-2 text-[12.5px] font-semibold">
            <span className={cn('h-[7px] w-[7px] rounded-full', openReports ? 'bg-warn' : 'bg-good')} />
            <span className={openReports ? 'text-warn' : 'text-good'}>
              {openReports ? 'Needs attention' : 'Healthy'}
            </span>
          </span>
        }
      />

      {/* The sections, as one segmented strip.

          inline-flex, not flex: as a block it stretched the full width of the
          screen and left a long empty tail of border past the last section,
          which read as a bar that had lost its contents. It is now as wide as
          what is in it. */}
      <div className="mb-5 inline-flex max-w-full flex-wrap gap-1 self-start rounded-[calc(12px*var(--radius-mul,1))] border border-line-soft bg-surface-1 p-1.5">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id as Tab)}
            className={cn(
              'flex h-[34px] items-center gap-2 rounded-[calc(8px*var(--radius-mul,1))] px-3.5 text-[13px] font-bold transition-colors',
              tab === t.id ? 'bg-accent text-accent-fg' : 'text-mute hover:bg-surface-2 hover:text-dim'
            )}
          >
            <Icon name={t.icon} size={14} />
            {t.label}
            {'badge' in t && t.badge ? (
              <span className={cn(
                'grid h-[17px] min-w-[17px] place-items-center rounded-full px-1 text-[10px] font-extrabold tabular-nums',
                tab === t.id ? 'bg-[rgba(0,0,0,.25)]' : 'bg-warn text-[var(--bg-0)]'
              )}>
                {t.badge}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      <div key={tab} className="panel-in flex w-full flex-col gap-[var(--gap)]">
        {tab === 'overview' && <Overview admin={overview.data} />}
        {tab === 'users' && <Users isAdmin={me.role === 'admin'} />}
        {tab === 'parties' && <Parties />}
        {tab === 'reports' && <Reports />}
        {tab === 'announce' && <Announcements />}
        {tab === 'emoji' && <EmojiAdmin />}
        {tab === 'perms' && <Permissions isAdmin={me.role === 'admin'} />}
        {tab === 'audit' && <Audit />}
      </div>
    </Page>
  );
}


/* ---------------------------------------------------------- overview */

function Overview({ admin }: { admin?: AdminOverview }) {
  const { data } = useQuery({
    queryKey: ['health'],
    queryFn: () => api.get<Record<string, number | string | boolean>>('/api/health'),
    refetchInterval: 10_000
  });

  const tiles: [string, string | number][] = data
    ? [
        ['Version', String(data.version)],
        ['Uptime', Math.round(Number(data.uptime) / 60) + ' min'],
        ['Accounts', Number(data.users)],
        ['Online', Number(data.online)],
        ['Open parties', Number(data.parties)],
        ['Messages', Number(data.messages)],
        ['Sockets', Number(data.sockets)]
      ]
    : [];

  return (
    <>
      {admin ? <Community admin={admin} /> : null}

    <Card>
      <CardHead title="Server" note="Refreshes every 10 seconds" />
      <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]">
        {tiles.map(([k, v]) => (
          <div key={k} className="rounded-[var(--radius)] border border-line-soft bg-surface-1 p-3.5">
            <div className="text-[10px] font-extrabold tracking-[.1em] text-ghost uppercase">{k}</div>
            <div className="mt-1 text-[20px] font-extrabold tabular-nums">{v}</div>
          </div>
        ))}
      </div>
    </Card>
    </>
  );
}

/* ------------------------------------------------------------- users */

/**
 * The people.
 *
 * A search box on its own answers "where is this person", which is only half
 * of what a moderator does - the other half is "who is suspended", "who just
 * arrived", "who has the keys". Those are the filters, and they count
 * themselves so the answer is on the button before you press it.
 */
type Cut = 'all' | 'staff' | 'suspended' | 'online' | 'new';

function Users({ isAdmin }: { isAdmin: boolean }) {
  const [q, setQ] = useState('');
  const [cut, setCut] = useState<Cut>('all');
  const [sort, setSort] = useState<'seen' | 'new' | 'level'>('seen');
  const [open, setOpen] = useState<PublicUser | null>(null);

  const { data, isPending } = useQuery({
    queryKey: ['admin-users', q],
    queryFn: () => api.get<{ users: PublicUser[] }>('/api/admin/users' + api.qs({ q }))
  });

  const all = data?.users ?? [];
  const week = Date.now() - 7 * 86_400_000;

  const matches = (u: PublicUser, which: Cut) =>
    which === 'staff' ? u.role !== 'user' :
      which === 'suspended' ? !!u.banned :
        which === 'online' ? u.status !== 'offline' && u.status !== 'invisible' :
          which === 'new' ? u.createdAt > week : true;

  const shown = all
    .filter((u) => matches(u, cut))
    .sort((a, b) =>
      sort === 'level' ? b.level - a.level :
        sort === 'new' ? b.createdAt - a.createdAt :
          (b.lastSeen || 0) - (a.lastSeen || 0));

  const cuts: [Cut, string][] = [
    ['all', 'Everyone'], ['online', 'Online'], ['staff', 'Staff'],
    ['suspended', 'Suspended'], ['new', 'New this week']
  ];

  return (
    <>
      <Card>
        <CardHead
          title="Users"
          note="Pick anybody to open their record."
          actions={<Tag>{shown.length}</Tag>}
        />

        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by username" spellCheck={false} />

        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          {cuts.map(([id, label]) => {
            const n = all.filter((u) => matches(u, id)).length;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setCut(id)}
                aria-pressed={cut === id}
                className={cn(
                  'flex h-[30px] items-center gap-1.5 rounded-[calc(10px*var(--radius-mul,1))] border px-3 text-[12px] font-bold transition-colors',
                  cut === id
                    ? 'border-accent-line bg-accent-soft text-accent-hi'
                    : 'border-line-soft bg-surface-1 text-mute hover:border-line hover:text-dim'
                )}
              >
                {label}
                <span className={cn('text-[11px] tabular-nums', cut === id ? 'opacity-70' : 'text-ghost')}>{n}</span>
              </button>
            );
          })}

          <span className="flex-1" />

          {/* Which end of the list you want first. */}
          {([['seen', 'Last seen'], ['new', 'Newest'], ['level', 'Level']] as const).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setSort(id)}
              className={cn(
                'h-[30px] rounded-[calc(10px*var(--radius-mul,1))] px-2.5 text-[11.5px] font-bold transition-colors',
                sort === id ? 'text-accent-hi' : 'text-ghost hover:text-mute'
              )}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="mt-[var(--gap)] flex flex-col gap-0.5">
          {isPending ? (
            Array.from({ length: 5 }, (_, i) => <div key={i} className="h-[54px] animate-pulse rounded-[calc(10px*var(--radius-mul,1))] bg-surface-1" />)
          ) : !shown.length ? (
            <Empty title="Nobody found" note={q ? 'Nothing matches that name.' : 'No accounts in this group.'} />
          ) : (
            shown.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => setOpen(u)}
                className="flex w-full items-center gap-3 rounded-[calc(10px*var(--radius-mul,1))] px-2.5 py-2 text-left transition-colors hover:bg-surface-1"
              >
                <Avatar user={u} size={34} presence />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <UserName user={u} className="text-[14px] font-semibold" />
                    <RoleTag role={u.role} />
                    <TierTag tier={u.tier} />
                    {u.banned ? <Tag kind="bad">SUSPENDED</Tag> : null}
                  </span>
                  <span className="block truncate font-mono text-[12px] text-mute">@{u.username}</span>
                </span>

                {/* When they were last here matters more than their level, so
                    it gets the line and the level gets the note. */}
                <span className="flex-none text-right">
                  <span className="block text-[12px] text-dim">{seen(u)}</span>
                  <span className="block text-[11px] text-ghost">Level {u.level}</span>
                </span>
              </button>
            ))
          )}
        </div>
      </Card>

      {open ? <UserSheet user={open} isAdmin={isAdmin} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

/** How long ago somebody was here, short enough to sit in a list. */
function seen(u: PublicUser): string {
  if (u.status !== 'offline' && u.status !== 'invisible') return 'Online now';
  if (!u.lastSeen) return 'Never';
  const mins = (Date.now() - u.lastSeen) / 60_000;
  if (mins < 1) return 'Just now';
  if (mins < 60) return Math.floor(mins) + 'm ago';
  if (mins < 1440) return Math.floor(mins / 60) + 'h ago';
  const days = Math.floor(mins / 1440);
  return days < 30 ? days + 'd ago' : Math.floor(days / 30) + 'mo ago';
}


/* ----------------------------------------------------------- parties */

function Parties() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();

  /*
   * Every party, including closed ones.
   *
   * /api/parties?scope=all only lists live ones, which is right for the app
   * and wrong here: a report raised a week later is about a party that has
   * closed, and staff have to be able to read what it said and who was in
   * it. Closed parties are off the pages people look at and still here.
   */
  const { data, isPending } = useQuery({
    queryKey: ['admin', 'parties', 'all'],
    queryFn: () => api.get<{ parties: Party[] }>('/api/admin/parties/all'),
    refetchInterval: 30000
  });

  async function act(path: string, body: unknown, note: string) {
    try {
      await api.post(path, body);
      toast(note, 'good');
      void queryClient.invalidateQueries({ queryKey: ['parties'] });
    } catch (err) { toast((err as Error).message, 'bad'); }
  }

  return (
    <Card>
      <CardHead title="Open parties" actions={<Tag>{data?.parties.length ?? 0}</Tag>} />
      <div className="flex flex-col gap-0.5">
        {isPending ? (
          Array.from({ length: 4 }, (_, i) => <div key={i} className="h-[54px] animate-pulse rounded-[calc(10px*var(--radius-mul,1))] bg-surface-1" />)
        ) : !data?.parties.length ? (
          <Empty title="Nothing open" />
        ) : (
          data.parties.map((p) => (
            <div key={p.id} className="flex items-center gap-3 rounded-[calc(10px*var(--radius-mul,1))] px-2.5 py-2 hover:bg-surface-1">
              <button type="button" onClick={() => go('party/' + p.id)} className="min-w-0 flex-1 text-left">
                <span className="flex items-center gap-2">
                  <span className="truncate text-[14px] font-semibold">{p.title}</span>
                  {p.boosted ? <Tag kind="gold">BOOSTED</Tag> : null}
                </span>
                <span className="block truncate text-[12.5px] text-mute">
                  {p.gameId} · {p.memberCount}/{p.size}
                </span>
              </button>
              <Button
                size="sm"
                kind={p.boosted ? 'ghost' : 'outline'}
                onClick={() => void act(`/api/admin/parties/${p.id}/boost`, { boosted: !p.boosted },
                  p.boosted ? 'Boost removed' : 'Boosted to the top of every list')}
              >
                {p.boosted ? 'Unboost' : 'Boost'}
              </Button>
              <Button
                size="sm"
                kind="danger"
                onClick={async () => {
                  const ok = await confirm('Close this party?', 'This is logged in the audit trail.', { confirmLabel: 'Close', danger: true });
                  if (ok) {
                    try {
                      await api.del(`/api/admin/parties/${p.id}`);
                      toast('Party closed', 'good');
                      void queryClient.invalidateQueries({ queryKey: ['parties'] });
                    } catch (err) { toast((err as Error).message, 'bad'); }
                  }
                }}
              >
                Close
              </Button>
            </div>
          ))
        )}
      </div>
      {dialog}
    </Card>
  );
}

/* ------------------------------------------------------------- audit */

function Audit() {
  const { data, isPending } = useQuery({
    queryKey: ['audit'],
    queryFn: () => api.get<{ entries: { id?: string; at: number; action: string; userId: string; meta: Record<string, unknown> }[] }>('/api/admin/audit')
  });

  return (
    <Card>
      <CardHead title="Audit trail" note="Newest first" />
      {isPending ? (
        <div className="h-[200px] animate-pulse rounded-[var(--radius)] bg-surface-1" />
      ) : !data?.entries.length ? (
        <Empty title="Nothing logged yet" />
      ) : (
        <div className="flex flex-col">
          {/*
            Sorted here rather than trusted from the response.

            The server already sends these newest first, and this reversed them
            again - so the panel labelled "Newest first" showed oldest first,
            and .slice(0, 100) then kept the oldest hundred of the two hundred
            sent, hiding the newest entries completely. Sorting on the
            timestamp makes the label true whatever order the response arrives
            in, and the id tie-break keeps entries written in the same
            millisecond in a stable order rather than shuffling between
            renders.
          */}
          {[...data.entries]
            .sort((x, y) => (y.at - x.at) || String(y.id ?? '').localeCompare(String(x.id ?? '')))
            .slice(0, 100)
            .map((e, i) => (
            <div key={i} className="flex items-center gap-3 border-b border-line-soft py-2 text-[12.5px] last:border-0">
              <span className={cn(
                'font-mono font-semibold',
                e.action.startsWith('admin') ? 'text-bad' : 'text-dim'
              )}>
                {e.action}
              </span>
              <span className="min-w-0 flex-1 truncate text-mute">
                {Object.entries(e.meta ?? {}).map(([k, v]) => `${k}=${String(v)}`).join(' ')}
              </span>
              <span className="flex-none text-ghost">{new Date(e.at).toLocaleString()}</span>
            </div>
            ))}
        </div>
      )}
    </Card>
  );
}
